import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Menu as MenuIcon, DarkMode, LightMode } from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../../shared/contexts/LanguageContext';
import { useLocalizedPath } from '../../shared/hooks/useLocalizedPath';
import { stripLanguage } from '../../shared/constants/routes';
import { useThemeMode } from '../../shared/contexts/ThemeContext';
import { LanguageSwitcher } from './LanguageSwitcher';
import { Footer } from './Footer';

const logoPath = '/MEsupportNetLogo.png';

const navItems = [
  { key: 'home', path: '/' },
  { key: 'about', path: '/about' },
  { key: 'whatIsME', path: '/what-is-me' },
  { key: 'mitmachen', path: '/mitmachen' },
  { key: 'donate', path: '/donate' },
];

const getNavText = (key: string, language: 'en' | 'de'): string => {
  const translations: Record<string, Record<'en' | 'de', string>> = {
    'home': { en: 'Home', de: 'Startseite' },
    'about': { en: 'About Us', de: 'Über uns' },
    'whatIsME': { en: 'What is ME', de: 'Was ist ME' },
    'mitmachen': { en: 'Join Us', de: 'Mitmachen' },
    'donate': { en: 'Donate', de: 'Spenden' },
  };
  return translations[key]?.[language] || key;
};

export const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const { language } = useLanguage();
  const lp = useLocalizedPath();
  const { path: currentPath } = stripLanguage(location.pathname);
  const { mode, toggleMode } = useThemeMode();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
      <Box sx={{ p: theme.spacing(2), display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Box
          component="img"
          src={logoPath}
          alt="ME SupportNet"
          sx={{ height: { xs: 30, sm: 40 }, width: 'auto', maxWidth: '100%', objectFit: 'contain' }}
        />
      </Box>
      <List>
        {navItems.map((item) => {
          const isActive = currentPath === item.path;
          return (
            <ListItem key={item.key} component={Link} to={lp(item.path)}>
              <ListItemText
                primary={getNavText(item.key, language)}
                sx={{
                  color: isActive ? 'primary.main' : 'text.primary',
                  fontWeight: isActive ? 600 : 400,
                  '& .MuiListItemText-primary': {
                    color: isActive ? 'primary.main' : 'text.primary',
                  },
                }}
              />
            </ListItem>
          );
        })}
      </List>
      <Box sx={{ p: theme.spacing(2), display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
        <IconButton
          onClick={toggleMode}
          color={mode === 'light' ? 'primary' : 'inherit'}
          aria-label={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          sx={{
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          {mode === 'dark' ? <LightMode /> : <DarkMode />}
        </IconButton>
        <LanguageSwitcher mobile />
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : 'background.paper',
          color: 'text.primary',
          borderRadius: 0,
          border: 'none',
          borderBottom: '1px solid',
          borderColor: 'divider',
          zIndex: theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{
          padding: { xs: theme.spacing(1, 2), md: theme.spacing(1, 4) },
          minHeight: { xs: '64px !important', md: '70px !important' },
        }}>
          {isMobile ? (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          ) : null}

          {!isMobile && (
            <Box
              component={Link}
              to={lp('/')}
              sx={{ flexGrow: 1, textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center' }}
            >
              <Box
                component="img"
                src={logoPath}
                alt="ME SupportNet"
                sx={{ height: { md: 40, lg: 55 }, width: 'auto', objectFit: 'contain', display: 'block' }}
              />
            </Box>
          )}

          {isMobile ? (
            <Box
              component={Link}
              to={lp('/')}
              sx={{ flexGrow: 1, textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}
            >
              <Box
                component="img"
                src={logoPath}
                alt="ME SupportNet"
                sx={{ height: 30, width: 'auto', objectFit: 'contain', display: 'block' }}
              />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: { md: 0, lg: 1 }, alignItems: 'center' }}>
              {navItems.map((item) => (
                <Typography
                  key={item.key}
                  component={Link}
                  to={lp(item.path)}
                  sx={{
                    color: currentPath === item.path ? 'primary.main' : 'text.primary',
                    fontWeight: currentPath === item.path ? 600 : 400,
                    textDecoration: 'none',
                    cursor: 'pointer',
                    px: { md: 1, lg: 2 },
                    py: 1,
                    borderRadius: 1,
                    '&:hover': {
                      color: 'primary.main',
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  {getNavText(item.key, language)}
                </Typography>
              ))}
              <IconButton
                onClick={toggleMode}
                color={mode === 'light' ? 'primary' : 'inherit'}
                aria-label={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                sx={{
                  ml: 0.5,
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                {mode === 'dark' ? <LightMode /> : <DarkMode />}
              </IconButton>
              <Box sx={{ ml: 0.5 }}>
                <LanguageSwitcher />
              </Box>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        anchor="left"
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240 },
        }}
      >
        {drawer}
      </Drawer>

      <Box component="main" sx={{
        flexGrow: 1,
        margin: 0,
        padding: 0,
        pt: { xs: '64px', md: '70px' }
      }}>
        {children}
      </Box>
      <Footer />
    </Box>
  );
};

